/**
 * EventDetailContent
 *
 * Split-screen layout for the event detail modal.
 * Left: Hero image. Right: Content, metadata, and actions.
 * No scroll design - everything fits in viewport.
 */

import React, { useContext } from 'react';
import { Icon } from 'semantic-ui-react';
import { GrownupsContext } from '../../GrownupsContext';
import { useEventsStore } from '../../stores/useEventsStore';
import useAuthStore from '../../stores/useAuthStore';
import { useEventModalStore } from '../../stores/useEventModalStore';
import { firestoreService } from '../../services/firestore.service';
import { celebrateActivityDrop } from '../Sidebar/celebrationUtils';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import EventParticipants from './EventParticipants';
import EventShareButton from './EventShareButton';

const EventDetailContent = ({ event, calendar, eventIndex, totalEvents }) => {
  const { experiencesCount, setExperiencesCount } = useContext(GrownupsContext);
  const setEvents = useEventsStore((state) => state.setEvents);
  const events = useEventsStore((state) => state.events);
  const user = useAuthStore((state) => state.user);
  const participantCounts = useEventModalStore((state) => state.participantCounts);

  // Get participant count - prefer from event data, fallback to store
  const participantCount = event.participantCount || (event.id ? (participantCounts[event.id] || 0) : 0);

  // Check if event is already in user's calendar
  const isEventAdded = events.some(
    (e) =>
      e.name === event.name &&
      e.sourceCalendarId === calendar.id
  );

  // Format date range
  const formatDateRange = () => {
    const start = event.startDate || event.date;
    const end = event.endDate;

    if (!start) return '';

    const startDate = moment(start);

    if (!end || start === end) {
      return startDate.format('MMMM D, YYYY');
    }

    const endDate = moment(end);

    // Same month
    if (startDate.month() === endDate.month() && startDate.year() === endDate.year()) {
      return `${startDate.format('MMMM D')}-${endDate.format('D, YYYY')}`;
    }

    // Different months
    return `${startDate.format('MMMM D')} - ${endDate.format('MMMM D, YYYY')}`;
  };

  // Handle adding event to calendar
  const handleJoinEvent = async () => {
    if (isEventAdded) return;

    // Create event data
    const eventData = {
      id: uuidv4(),
      title: event.name,
      name: event.name,
      type: 'activity',
      start: moment(event.startDate || event.date).startOf('day').toDate(),
      end: moment(event.endDate || event.startDate || event.date).endOf('day').toDate(),
      isAllDay: true,
      isDraggable: true,
      location: event.location || '',
      description: event.vibe || '',
      category: event.category,
      fromPreMadeCalendar: true,
      sourceCalendarId: calendar.id,
      sourceCalendarName: calendar.name,
      sourceCalendarEmoji: calendar.emoji,
    };

    // Add to events
    setEvents((prev) => [...prev, eventData]);

    // Celebrate
    const newCount = (experiencesCount || 0) + 1;
    setExperiencesCount(newCount);
    celebrateActivityDrop(newCount);

    // Track in Firestore
    firestoreService.trackEventAdded(event.name, event.category, user?.uid);
    firestoreService.trackCalendarUsed(calendar.id, calendar.name, user?.uid);

    // Track participation (for the participant count)
    if (event.id) {
      firestoreService.trackEventParticipation(event.id, calendar.id, user?.uid);
    }
  };

  // Get detail info or fallback
  const detail = event.detail || {};
  const hasDetail = !!event.detail;
  const categoryLabel = detail.categoryLabel || event.category || 'Event';
  const emoji = detail.emoji || calendar.emoji;
  const description = detail.description || event.vibe || '';
  const quickFacts = detail.quickFacts || [];
  const isHighlight = detail.isHighlight || false;
  const images = detail.images || [];
  const heroImage = images[0] || calendar.heroImage;

  return (
    <article className="event-detail-article">
      {/* Left side: Hero image */}
      <div className="event-hero">
        {heroImage ? (
          <div className="event-hero-image-container">
            <img
              src={heroImage}
              alt={`${event.name} - ${event.location}`}
              className="event-hero-image"
              loading="eager"
            />
            <div className="event-hero-overlay" />
          </div>
        ) : (
          <div className="event-hero-placeholder">
            <span className="event-hero-emoji">{emoji}</span>
          </div>
        )}
      </div>

      {/* Right side: Content */}
      <div className="event-content">
        {/* Top bar with category badge and participant count */}
        <div className="event-top-bar">
          <div className="event-category-badge">
            {isHighlight && <Icon name="star" />}
            <span>{categoryLabel}</span>
          </div>

          {participantCount > 0 && (
            <div className="event-participant-badge">
              <span>👥 {participantCount.toLocaleString()} joined</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 id="event-modal-title" className="event-title">
          {event.name}
        </h1>

        {/* Meta info */}
        <div className="event-meta">
          <div className="event-meta-item">
            <Icon name="calendar alternate outline" />
            <span>{formatDateRange()}</span>
          </div>
          {event.location && (
            <div className="event-meta-item">
              <Icon name="map marker alternate" />
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {/* Event counter - which calendar, position */}
        <div className="event-counter">
          <span className="event-counter-calendar">
            {calendar.emoji} {calendar.name}
          </span>
          <span className="event-counter-position">
            Event {eventIndex + 1} of {totalEvents}
          </span>
        </div>

        {/* Body content */}
        <div className="event-body">
          {/* Vibe / Short description */}
          {event.vibe && (
            <p className="event-vibe">{event.vibe}</p>
          )}

          {/* Quick facts */}
          {quickFacts.length > 0 && (
            <div className="event-quick-facts">
              {quickFacts.map((fact, idx) => (
                <div key={idx} className="event-fact">
                  <span className="event-fact-label">{fact.label}</span>
                  <span className="event-fact-value">{fact.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Full description */}
          {hasDetail && description && (
            <div className="event-description">
              {description.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          )}

          {/* No detail message */}
          {!hasDetail && (
            <p className="event-no-detail">
              More details coming soon! This event is part of the {calendar.name} calendar.
            </p>
          )}
        </div>

        {/* Actions at bottom of content area */}
        <div className="event-actions">
          <button
            className={`event-join-button ${isEventAdded ? 'added' : ''}`}
            onClick={handleJoinEvent}
            disabled={isEventAdded}
          >
            {isEventAdded ? (
              <>
                <Icon name="check" />
                <span>Added to Calendar</span>
              </>
            ) : (
              <>
                <Icon name="plus" />
                <span>Add to My Calendar</span>
              </>
            )}
          </button>

          {event.ticketUrl && (
            <a
              href={event.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="event-ticket-button"
            >
              <Icon name="ticket" />
              <span>Get Tickets</span>
            </a>
          )}

          <EventShareButton event={event} calendar={calendar} />
        </div>
      </div>
    </article>
  );
};

export default EventDetailContent;
